import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Palette,
    Type,
    MousePointerClick,
    TextCursorInput,
    BadgeCheck,
    LayoutGrid,
    Table2,
    MessageSquare,
    PanelsTopLeft,
    Boxes,
    Check,
    Copy,
    Fingerprint,
    Wallet,
    Users,
    ShoppingCart,
    Search,
    Bell,
    ChevronDown,
    Home,
    CreditCard,
    Settings,
    Loader2,
    Utensils,
    ShieldCheck,
} from "lucide-react";

import { BioButton } from "@/components/bio/BioButton";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { MetricCard } from "@/components/bio/MetricCard";
import { EmptyState } from "@/components/bio/EmptyState";
import { SectionCard, SpecRow } from "@/components/bio/SectionCard";
import { formatUSD, formatDateTime } from "@/lib/format";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

const LOGO = "https://biofoodsoftware.co/images/new/logo-biofood-new.png";
const HUELLA = "https://biofoodsoftware.co/images/new/huella_blanca.png";

const NAV = [
    { id: "brand-dna", label: "Identidad y Tokens", icon: Palette },
    { id: "typography", label: "Tipografía", icon: Type },
    { id: "buttons", label: "Botones", icon: MousePointerClick },
    { id: "forms", label: "Formularios", icon: TextCursorInput },
    { id: "badges", label: "Badges de estado", icon: BadgeCheck },
    { id: "metrics", label: "Tarjetas métricas", icon: LayoutGrid },
    { id: "table", label: "Tablas y listas", icon: Table2 },
    { id: "overlays", label: "Modales y toasts", icon: MessageSquare },
    { id: "layout", label: "Navegación", icon: PanelsTopLeft },
    { id: "states", label: "Vacío y skeleton", icon: Boxes },
];

const COLORS = [
    { name: "Primary", val: "#00508A", note: ".btn-lf-primary / forgot-link" },
    { name: "Primary Dark", val: "#003A66", note: "gradiente de botón" },
    { name: "Accent", val: "#2A87C8", note: "focus / hover borde" },
    { name: "Accent Mid", val: "#1464A0", note: "hover texto" },
    { name: "Accent Light", val: "#7DCEF4", note: ".lf-tagline span" },
    { name: "Success", val: "#1BC5BD", note: "Metronic success" },
    { name: "Warning", val: "#F5A623", note: "alerta cookie" },
    { name: "Error", val: "#B71C1C", note: "alert-danger" },
    { name: "Background", val: "#F8FAFC", note: "input bg / fondo" },
    { name: "Surface", val: "#FFFFFF", note: ".lf-card" },
    { name: "Border", val: "#E2E8F0", note: "input border" },
    { name: "Text", val: "#1E3A5F", note: "texto de input" },
    { name: "Text Secondary", val: "#6B7A99", note: "labels" },
];

const SHADOWS = [
    { name: "shadow-bio-sm", css: "0 2px 8px rgba(0,0,0,.06)" },
    { name: "shadow-bio-card", css: "0 4px 12px rgba(42,135,200,.14)" },
    { name: "shadow-bio-btn", css: "0 4px 14px rgba(0,80,138,.4)" },
    { name: "shadow-bio-modal", css: "0 20px 60px rgba(0,0,0,.25)" },
];

const RADII = [
    { name: "input / button", val: "10px", cls: "rounded-bio" },
    { name: "card", val: "16px", cls: "rounded-bio-card" },
    { name: "modal / card-lg", val: "20px", cls: "rounded-bio-lg" },
    { name: "pill / avatar", val: "9999px", cls: "rounded-full" },
];

function ColorSwatch({ c }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(c.val);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };
    return (
        <button
            onClick={copy}
            data-testid={`swatch-${c.val.replace("#", "")}`}
            className="group text-left rounded-bio-card border border-bio-border-subtle overflow-hidden bg-white hover:shadow-bio-card transition-shadow duration-200"
        >
            <div className="h-16 w-full relative" style={{ backgroundColor: c.val }}>
                <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {copied ? (
                        <Check className="h-4 w-4 text-white drop-shadow" />
                    ) : (
                        <Copy className="h-4 w-4 text-white/80 drop-shadow" />
                    )}
                </span>
            </div>
            <div className="p-3">
                <p className="text-sm font-semibold text-bio-text">{c.name}</p>
                <p className="text-xs font-mono text-bio-text-secondary uppercase">{c.val}</p>
                <p className="text-[11px] text-bio-text-muted mt-1 truncate">{c.note}</p>
            </div>
        </button>
    );
}

const TXN = [
    { id: "4821", name: "María González", grade: "5°B", item: "Almuerzo Balanceado", amt: 4.5, status: "confirmed", bio: "QR" },
    { id: "4822", name: "Andrés Beltrán", grade: "3°A", item: "Jugo Natural + Fruta", amt: 2.25, status: "pending", bio: "Huella" },
    { id: "4823", name: "Sofía Ramírez", grade: "7°C", item: "Sándwich Integral", amt: 3.0, status: "rejected", bio: "Huella" },
    { id: "4824", name: "Juan David Cruz", grade: "4°A", item: "Ensalada de Frutas", amt: 2.75, status: "refunded", bio: "QR" },
];

export default function Styleguide() {
    const [active, setActive] = useState("brand-dna");
    const [rechargeOpen, setRechargeOpen] = useState(false);
    const [loadingBtn, setLoadingBtn] = useState(false);

    const go = (id) => {
        setActive(id);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const demoLoad = () => {
        setLoadingBtn(true);
        setTimeout(() => setLoadingBtn(false), 1800);
    };

    return (
        <div className="min-h-screen bg-bio-bg text-bio-text font-poppins">
            {/* Topbar */}
            <header className="sticky top-0 z-40 h-[68px] border-b border-bio-border-subtle bg-white/95 backdrop-blur-md">
                <div className="h-full px-4 md:px-8 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-bio bg-bio-gradient flex items-center justify-center shrink-0">
                            <img src={HUELLA} alt="" className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="font-extrabold tracking-tight text-bio-primary leading-none">BIOFOOD</p>
                            <p className="text-[11px] text-bio-text-secondary truncate">Sistema de diseño · Fase 1</p>
                        </div>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#EEF7FC] text-bio-primary">
                        <ShieldCheck className="h-3.5 w-3.5" /> Tokens extraídos del sitio oficial
                    </span>
                </div>
            </header>

            <div className="mx-auto max-w-[1400px] flex">
                {/* Sidebar */}
                <aside className="hidden lg:block w-[260px] shrink-0 sticky top-[68px] h-[calc(100vh-68px)] border-r border-bio-border-subtle bg-white px-4 py-6 overflow-y-auto">
                    <nav className="space-y-1">
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                data-testid={`nav-${n.id}`}
                                onClick={() => go(n.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-bio text-sm font-semibold transition-colors ${
                                    active === n.id
                                        ? "bg-[#EEF7FC] text-bio-primary border-l-4 border-bio-primary"
                                        : "text-bio-text-secondary hover:bg-bio-bg-alt hover:text-bio-primary"
                                }`}
                            >
                                <n.icon className="h-4 w-4 shrink-0" />
                                <span className="truncate">{n.label}</span>
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Content */}
                <main className="flex-1 min-w-0 px-4 md:px-8 py-8 md:py-10 space-y-12">
                    {/* Hero */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-bio-lg bg-bio-hero text-white p-8 md:p-12 shadow-bio-modal relative overflow-hidden"
                    >
                        <img src={LOGO} alt="BIOFOOD" className="h-7 mb-6 brightness-0 invert" />
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight max-w-2xl">
                            Gestión inteligente de <span className="text-bio-accent-light">cafeterías escolares</span>
                        </h1>
                        <p className="mt-4 text-white/80 max-w-xl leading-relaxed">
                            Biblioteca de componentes y tokens de diseño — fuente única de verdad para todos los módulos
                            de la plataforma (POS, kiosco, portal de padres, dashboards y dispositivos).
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <BioButton variant="primary" className="bg-white !text-bio-primary bg-none shadow-lg hover:bg-white" onClick={() => go("brand-dna")}>
                                Explorar tokens
                            </BioButton>
                            <BioButton variant="secondary" className="!bg-transparent !border-white/60 !text-white hover:!bg-white/10" onClick={() => go("buttons")}>
                                Ver componentes
                            </BioButton>
                        </div>
                    </motion.div>

                    {/* Brand DNA */}
                    <SectionCard
                        id="brand-dna"
                        title="Identidad de marca y tokens"
                        subtitle="Paleta, sombras y radios extraídos directamente del CSS de biofoodsoftware.co. Haz clic en un color para copiar el hex."
                    >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {COLORS.map((c) => (
                                <ColorSwatch key={c.val} c={c} />
                            ))}
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mt-8">
                            <div className="space-y-4">
                                <SpecRow label="Gradiente de marca (botones)">
                                    <div className="h-12 w-full rounded-bio bg-bio-gradient shadow-bio-btn" />
                                </SpecRow>
                                <SpecRow label="Gradiente hero">
                                    <div className="h-12 w-full rounded-bio bg-bio-hero" />
                                </SpecRow>
                            </div>
                            <div className="space-y-4">
                                <SpecRow label="Radios de borde">
                                    {RADII.map((r) => (
                                        <div key={r.name} className="flex flex-col items-center gap-1">
                                            <div className={`h-12 w-12 bg-[#EEF7FC] border border-bio-accent/40 ${r.cls}`} />
                                            <span className="text-[11px] text-bio-text-secondary">{r.val}</span>
                                        </div>
                                    ))}
                                </SpecRow>
                            </div>
                        </div>

                        <div className="mt-8">
                            <SpecRow label="Elevaciones / sombras">
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                                    {SHADOWS.map((s) => (
                                        <div key={s.name} className="text-center">
                                            <div className="h-20 rounded-bio-card bg-white" style={{ boxShadow: s.css }} />
                                            <p className="text-xs font-semibold text-bio-text mt-3">{s.name}</p>
                                            <p className="text-[10px] font-mono text-bio-text-muted break-all">{s.css}</p>
                                        </div>
                                    ))}
                                </div>
                            </SpecRow>
                        </div>
                    </SectionCard>

                    {/* Typography */}
                    <SectionCard id="typography" title="Tipografía" subtitle="Familia Poppins (300–800). Escala responsiva mobile-first.">
                        <div className="space-y-5 divide-y divide-bio-border-subtle">
                            <div className="pt-0">
                                <p className="text-xs font-mono text-bio-text-muted mb-1">H1 · text-4xl lg:text-5xl · 700</p>
                                <p className="text-4xl lg:text-5xl font-bold tracking-tight">Cafeterías inteligentes</p>
                            </div>
                            <div className="pt-5">
                                <p className="text-xs font-mono text-bio-text-muted mb-1">H2 · text-2xl lg:text-3xl · 600</p>
                                <p className="text-2xl lg:text-3xl font-semibold tracking-tight">Resumen del día</p>
                            </div>
                            <div className="pt-5">
                                <p className="text-xs font-mono text-bio-text-muted mb-1">H3 · text-xl · 600</p>
                                <p className="text-xl font-semibold">Transacciones recientes</p>
                            </div>
                            <div className="pt-5">
                                <p className="text-xs font-mono text-bio-text-muted mb-1">Body · text-base · 400</p>
                                <p className="text-base leading-relaxed max-w-2xl text-bio-text-secondary">
                                    Tecnología biométrica, QR, POS, autoservicio, dispensadoras inteligentes y pagos
                                    digitales integrados en una sola plataforma.
                                </p>
                            </div>
                            <div className="pt-5">
                                <p className="text-xs font-mono text-bio-text-muted mb-1">Caption · text-xs · 500 · uppercase</p>
                                <p className="text-xs font-medium uppercase tracking-wider text-bio-text-secondary">Saldo del monedero</p>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Buttons */}
                    <SectionCard id="buttons" title="Botones" subtitle="Variantes y estados: default, hover, focus, disabled y loading. Altura 48px, radio 10px.">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-secondary">Primario</p>
                                <BioButton data-testid="btn-primary"><Wallet className="h-4 w-4" />Recargar saldo</BioButton>
                                <BioButton disabled>Deshabilitado</BioButton>
                                <BioButton loading={loadingBtn} onClick={demoLoad} data-testid="btn-loading">
                                    {loadingBtn ? "Procesando…" : "Probar loading"}
                                </BioButton>
                            </div>
                            <div className="space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-secondary">Secundario</p>
                                <BioButton variant="secondary"><Search className="h-4 w-4" />Buscar</BioButton>
                                <BioButton variant="secondary" disabled>Deshabilitado</BioButton>
                            </div>
                            <div className="space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-secondary">Ghost</p>
                                <BioButton variant="ghost">Cancelar</BioButton>
                                <BioButton variant="ghost" disabled>Deshabilitado</BioButton>
                            </div>
                            <div className="space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-secondary">Destructivo</p>
                                <BioButton variant="destructive">Eliminar</BioButton>
                                <BioButton variant="destructive" disabled>Deshabilitado</BioButton>
                            </div>
                        </div>
                        <div className="mt-8 flex flex-wrap items-end gap-4">
                            <div className="space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-secondary">Tamaños</p>
                                <div className="flex flex-wrap items-end gap-3">
                                    <BioButton size="sm">Compacto</BioButton>
                                    <BioButton size="md">Estándar</BioButton>
                                    <BioButton size="kiosk"><Fingerprint className="h-5 w-5" />Kiosco</BioButton>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Forms */}
                    <SectionCard id="forms" title="Controles de formulario" subtitle="Inputs 48px, borde 1.5px, foco con anillo #2A87C8. Incluye estados de error.">
                        <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-bio-text">Correo electrónico</Label>
                                <Input data-testid="input-email" placeholder="padre@colegio.edu.co" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-bio-text">Monto a recargar (USD)</Label>
                                <Input data-testid="input-amount" defaultValue="$25.00" className="h-12 rounded-bio bg-bio-bg border-[1.5px] font-semibold" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-bio-text">Deshabilitado</Label>
                                <Input disabled placeholder="No editable" className="h-12 rounded-bio" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-bio-error">Con error</Label>
                                <Input data-testid="input-error" defaultValue="correo-invalido" className="h-12 rounded-bio border-[1.5px] border-bio-error bg-[#FDECEA]/30 text-bio-error focus-visible:ring-bio-error/20" />
                                <p className="text-xs text-bio-error">Ingresa un correo electrónico válido.</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-bio-text">Sede</Label>
                                <Select>
                                    <SelectTrigger data-testid="select-campus" className="h-12 rounded-bio bg-bio-bg border-[1.5px]">
                                        <SelectValue placeholder="Seleccionar sede" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="norte">Colegio San Mateo · Sede Norte</SelectItem>
                                        <SelectItem value="sur">Colegio San Mateo · Sede Sur</SelectItem>
                                        <SelectItem value="central">Gimnasio Los Robles · Central</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-col justify-end gap-4 pb-1">
                                <div className="flex items-center gap-3">
                                    <Switch data-testid="switch-alerts" defaultChecked />
                                    <span className="text-sm text-bio-text">Alertas de consumo por correo</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Checkbox data-testid="check-terms" defaultChecked id="terms" />
                                    <Label htmlFor="terms" className="text-sm text-bio-text">Acepto consentimiento biométrico</Label>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    {/* Badges */}
                    <SectionCard id="badges" title="Badges de estado" subtitle="Estados de pago y de dispositivos/inventario con punto indicador.">
                        <div className="flex flex-wrap gap-3">
                            <StatusBadge status="pending" />
                            <StatusBadge status="confirmed" />
                            <StatusBadge status="rejected" />
                            <StatusBadge status="refunded" />
                            <StatusBadge status="online" />
                            <StatusBadge status="offline" />
                            <StatusBadge status="low_stock" />
                        </div>
                    </SectionCard>

                    {/* Metrics */}
                    <SectionCard id="metrics" title="Tarjetas métricas (KPIs)" subtitle="Indicadores del dashboard ejecutivo con tendencia.">
                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
                            <MetricCard label="Ventas de hoy" value={formatUSD(1284.5)} icon={ShoppingCart} trend={12} trendLabel="vs. ayer" />
                            <MetricCard label="Transacciones" value="326" icon={CreditCard} trend={8} trendLabel="vs. ayer" />
                            <MetricCard label="Estudiantes activos" value="412" icon={Users} trend={-3} trendLabel="vs. ayer" />
                            <MetricCard label="Saldo total wallets" value={formatUSD(18940)} icon={Wallet} trend={5} trendLabel="esta semana" />
                        </div>
                    </SectionCard>

                    {/* Table */}
                    <SectionCard id="table" title="Tabla de datos y lista móvil" subtitle="Tabla completa en escritorio; se transforma en tarjetas apiladas en móvil (sin scroll horizontal).">
                        {/* Desktop table */}
                        <div className="hidden md:block rounded-[12px] border border-bio-border-subtle overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-bio-bg hover:bg-bio-bg">
                                        <TableHead className="text-xs font-bold uppercase tracking-wider text-bio-text-secondary">Estudiante</TableHead>
                                        <TableHead className="text-xs font-bold uppercase tracking-wider text-bio-text-secondary">ID · Método</TableHead>
                                        <TableHead className="text-xs font-bold uppercase tracking-wider text-bio-text-secondary">Producto</TableHead>
                                        <TableHead className="text-xs font-bold uppercase tracking-wider text-bio-text-secondary text-right">Monto</TableHead>
                                        <TableHead className="text-xs font-bold uppercase tracking-wider text-bio-text-secondary">Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {TXN.map((t) => (
                                        <TableRow key={t.id} className="hover:bg-bio-bg-alt/60">
                                            <TableCell>
                                                <div className="font-semibold text-bio-text">{t.name}</div>
                                                <div className="text-xs text-bio-text-muted">{t.grade}</div>
                                            </TableCell>
                                            <TableCell className="text-sm text-bio-text-secondary">
                                                #{t.id} · {t.bio}
                                            </TableCell>
                                            <TableCell className="text-sm text-bio-text">{t.item}</TableCell>
                                            <TableCell className="text-right font-semibold text-bio-text">{formatUSD(t.amt)}</TableCell>
                                            <TableCell><StatusBadge status={t.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {/* Mobile list */}
                        <div className="md:hidden space-y-3">
                            {TXN.map((t) => (
                                <div key={t.id} className="rounded-bio-card border border-bio-border-subtle p-4 bg-white">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-semibold text-bio-text truncate">{t.name}</p>
                                            <p className="text-xs text-bio-text-muted">{t.grade} · #{t.id} · {t.bio}</p>
                                        </div>
                                        <StatusBadge status={t.status} />
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <span className="text-sm text-bio-text-secondary truncate">{t.item}</span>
                                        <span className="font-semibold text-bio-text">{formatUSD(t.amt)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    {/* Overlays */}
                    <SectionCard id="overlays" title="Modales, toasts y notificaciones" subtitle="Diálogo de recarga en MODO DEMO y notificaciones sonner con acento de marca.">
                        <div className="flex flex-wrap gap-3">
                            <BioButton data-testid="open-recharge" onClick={() => setRechargeOpen(true)}>
                                <Wallet className="h-4 w-4" /> Abrir modal de recarga
                            </BioButton>
                            <BioButton variant="secondary" onClick={() => toast.success("Recarga confirmada", { description: "Se acreditaron $25.00 al monedero de María." })}>
                                Toast éxito
                            </BioButton>
                            <BioButton variant="secondary" onClick={() => toast.error("Pago rechazado", { description: "El proveedor rechazó la transacción." })}>
                                Toast error
                            </BioButton>
                            <BioButton variant="ghost" onClick={() => toast.info("MODO DEMO", { description: "RapidPay Pro simulado — pendiente de credenciales." })}>
                                Toast info
                            </BioButton>
                        </div>
                    </SectionCard>

                    {/* Layout / navigation */}
                    <SectionCard id="layout" title="Navegación y tabs" subtitle="Patrones de sidebar, barra superior y tabs reutilizables.">
                        <Tabs defaultValue="resumen" className="w-full">
                            <TabsList className="bg-[#EEF2F8] rounded-[12px] p-1">
                                <TabsTrigger value="resumen" data-testid="tab-resumen" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-bio-primary data-[state=active]:shadow-sm font-semibold">Resumen</TabsTrigger>
                                <TabsTrigger value="menu" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-bio-primary data-[state=active]:shadow-sm font-semibold">Menú</TabsTrigger>
                                <TabsTrigger value="alertas" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-bio-primary data-[state=active]:shadow-sm font-semibold">Alertas</TabsTrigger>
                            </TabsList>
                            <TabsContent value="resumen" className="pt-4 text-sm text-bio-text-secondary">Tarjetas de resumen del colegio y actividad reciente.</TabsContent>
                            <TabsContent value="menu" className="pt-4 text-sm text-bio-text-secondary"><span className="inline-flex items-center gap-2"><Utensils className="h-4 w-4 text-bio-primary" /> Menú del día con información nutricional.</span></TabsContent>
                            <TabsContent value="alertas" className="pt-4 text-sm text-bio-text-secondary">Alertas de saldo bajo y restricciones alimentarias.</TabsContent>
                        </Tabs>

                        <div className="mt-8 grid lg:grid-cols-[220px_1fr] gap-4 rounded-bio-card border border-bio-border-subtle overflow-hidden">
                            <div className="bg-white border-r border-bio-border-subtle p-3 space-y-1">
                                {[
                                    { icon: Home, label: "Inicio", active: true },
                                    { icon: Wallet, label: "Recargas" },
                                    { icon: Users, label: "Mis hijos" },
                                    { icon: Settings, label: "Ajustes" },
                                ].map((i) => (
                                    <div key={i.label} className={`flex items-center gap-3 px-4 py-3 rounded-bio text-sm font-semibold ${i.active ? "bg-[#EEF7FC] text-bio-primary border-l-4 border-bio-primary" : "text-bio-text-secondary"}`}>
                                        <i.icon className="h-4 w-4" />{i.label}
                                    </div>
                                ))}
                            </div>
                            <div className="p-4">
                                <div className="h-[52px] rounded-bio bg-white border border-bio-border-subtle flex items-center justify-between px-4">
                                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-bio-text">Colegio San Mateo <ChevronDown className="h-4 w-4 text-bio-text-muted" /></span>
                                    <span className="inline-flex items-center gap-3 text-bio-text-secondary">
                                        <Bell className="h-4 w-4" />
                                        <span className="h-8 w-8 rounded-full bg-bio-gradient" />
                                    </span>
                                </div>
                                <p className="text-xs text-bio-text-muted mt-3">Vista previa de sidebar + barra superior.</p>
                            </div>
                        </div>
                    </SectionCard>

                    {/* States */}
                    <SectionCard id="states" title="Estados vacíos y skeletons" subtitle="Toda vista debe tener estado de carga, vacío y error.">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="rounded-bio-card border border-bio-border-subtle">
                                <EmptyState
                                    icon={ShoppingCart}
                                    title="Sin transacciones hoy"
                                    description="Aún no se registran compras en esta cafetería. Aparecerán aquí en tiempo real."
                                    action={<BioButton variant="secondary" size="sm">Actualizar</BioButton>}
                                />
                            </div>
                            <div className="rounded-bio-card border border-bio-border-subtle p-6 space-y-4">
                                <div className="flex items-center gap-3">
                                    <Skeleton className="h-11 w-11 rounded-bio" />
                                    <div className="space-y-2 flex-1">
                                        <Skeleton className="h-4 w-1/2" />
                                        <Skeleton className="h-3 w-1/3" />
                                    </div>
                                </div>
                                <Skeleton className="h-24 w-full rounded-bio" />
                                <div className="flex gap-3">
                                    <Skeleton className="h-10 flex-1 rounded-bio" />
                                    <Skeleton className="h-10 flex-1 rounded-bio" />
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    <footer className="pt-4 pb-8 text-center text-xs text-bio-text-muted">
                        BIOFOOD · Sistema de diseño Fase 1 — tokens extraídos de biofoodsoftware.co · Poppins · USD · dd/mm/aaaa
                    </footer>
                </main>
            </div>

            {/* Recharge demo modal */}
            <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
                <DialogContent className="rounded-bio-lg max-w-md border-bio-border-subtle">
                    <DialogHeader>
                        <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#FEF6E9] text-[#B36B00] mb-2">
                            MODO DEMO · RapidPay Pro
                        </span>
                        <DialogTitle className="text-xl font-bold text-bio-text">Recargar saldo</DialogTitle>
                        <DialogDescription className="text-bio-text-secondary">
                            Monedero de <span className="font-semibold text-bio-text">María González</span> · Saldo actual {formatUSD(12.5)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-3 gap-3 py-2">
                        {[10, 25, 50].map((v) => (
                            <button key={v} data-testid={`quick-amount-${v}`} className="h-14 rounded-bio border-[1.5px] border-bio-border font-bold text-bio-text hover:border-bio-accent hover:bg-[#EEF7FC] hover:text-bio-primary transition-colors">
                                {formatUSD(v)}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-bio-text-muted">Último movimiento: {formatDateTime(new Date())}</p>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <BioButton variant="ghost" onClick={() => setRechargeOpen(false)}>Cancelar</BioButton>
                        <BioButton
                            data-testid="confirm-recharge"
                            onClick={() => {
                                setRechargeOpen(false);
                                toast.success("Recarga confirmada (demo)", { description: "Se simuló la acreditación de $25.00." });
                            }}
                        >
                            Continuar al pago
                        </BioButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
